/**
 * Runtime polyfills for JavaScript APIs our dependencies call but that are
 * missing from older-but-still-common browsers. pdf.js 5.x (via react-pdf)
 * uses both of these on the main thread, so without them the client-copy
 * review PDF crashes ("URL.parse is not a function" on Safari < 18,
 * "Promise.withResolvers is not a function" on Safari < 17.4) and clients
 * can never reach the signing step.
 *
 * This module must stay the FIRST import in src/main.tsx.
 *
 * The pdf.js worker runs in its own context and cannot see these polyfills —
 * that is why public/pdf.worker.min.mjs is the LEGACY pdfjs-dist build, which
 * bundles its own compatibility layer (see the workerSrc comment in
 * DocumentViewer.tsx).
 */

// Promise.withResolvers — Safari 17.4+ / Chrome 119+ / Firefox 121+
// (typed via cast because tsconfig lib is ES2022)
const PromiseCtor = Promise as PromiseConstructor & {
  withResolvers?: <T>() => {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: unknown) => void;
  };
};

if (typeof PromiseCtor.withResolvers !== 'function') {
  // Defined via Object.defineProperty (non-enumerable) to match the native
  // static's property descriptor, rather than direct assignment which would
  // create an enumerable property.
  Object.defineProperty(PromiseCtor, 'withResolvers', {
    value: <T>() => {
      let resolve!: (value: T | PromiseLike<T>) => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// URL.parse — Safari 18+ / Chrome 126+ / Firefox 126+
const URLCtor = URL as typeof URL & {
  parse?: (url: string | URL, base?: string | URL) => URL | null;
};

if (typeof URLCtor.parse !== 'function') {
  // Non-enumerable to match native URL.parse's property descriptor (see the
  // Promise.withResolvers note above).
  Object.defineProperty(URLCtor, 'parse', {
    value: (url: string | URL, base?: string | URL): URL | null => {
      try {
        return new URL(url, base);
      } catch {
        return null;
      }
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

export {};
